<template>
  <div>
    <UContainer>
      <!-- Направления спорта -->
      <UCard
        v-if="directions?.length"
        class="common-mb"
        title="Направления спорта"
        :ui="{ title: 'text-size-h5 font-bold font-lato!' }"
      >
        <div class="mb-8">
          <ExpandableList
            v-slot="{ displayItems }"
            :limit="6"
            :items="directions"
          >
            <div
              class="grid grid-cols-3 max-xl:grid-cols-2 max-md:grid-cols-1 gap-5 mb-5 max-xl:gap-4"
            >
              <SmallCard
                v-for="(direction, index) in displayItems"
                :key="index"
                class="max-xl:h-auto"
                :top="direction.name"
                :slug="direction.slug"
                direction-size="small"
                bottom-top
                :to="'/directions/' + direction.id"
                no-link
                :ui="{
                  root: 'bg-background-bg1',
                  slug: 'shrink-0',
                  body: 'max-xl:p-5! max-md:p-3! gap-5',
                  top: 'typo-heading-h5! mb-2.5!',
                  link: 'hidden',
                }"
              >
                <template #bottom>
                  <div class="flex flex-wrap gap-1">
                    <UBadge
                      v-for="lesson in lessonTypes"
                      :key="lesson.id"
                      :label="lesson.name"
                    />
                  </div>
                </template>
              </SmallCard>
            </div>
          </ExpandableList>
        </div>

        <h5 class="text-size-h5 font-bold font-lato! mb-4">Документы (мок)</h5>

        <ExpandableList
          v-slot="{ displayItems }"
          :limit="3"
          :items="[1, 2, 3, 4, 5]"
        >
          <div
            class="grid grid-cols-3 max-xl:grid-cols-2 max-md:grid-cols-1 gap-5"
          >
            <SmallCard
              v-for="(item, index) in displayItems"
              :key="index"
              icon="i-local-file-award"
              top="Моковая Лицензия"
              no-link
              :ui="{
                root: 'bg-background-bg1',
                top: 'typo-paragraph-sm-regular mb-0.5!',
                bottom: 'typo-paragraph-md-regular',
                iconWrapper: 'w-8 h-8 bg-info text-white',
              }"
            >
              <template #bottom>
                <a class="text-accent-default font-medium" href="#">
                  Мок ЛИЦ-2024-001
                </a>
              </template>
            </SmallCard>
          </div>
        </ExpandableList>
      </UCard>

      <!-- О тренере -->
      <AboutTrainerSection
        class="common-mb"
        :images="[
          'https://picsum.photos/430/400',
          'https://picsum.photos/630/400',
          'https://picsum.photos/630/400',
          'https://picsum.photos/630/400',
        ]"
        :description="MOCK_TRAINER_DESCRIPTION"
      />

      <!-- Цитата -->
      <QuoteBlock
        class="common-mb"
        quote="«Моковый спорт — это не просто физическая активность, а ключевой элемент формирования характера и командного духа. Мы нацелены развивать в наших учениках не только спортивные навыки, но и жизненные ценности»."
        author-name="Алексей Моков"
        author-role="Тренер по моку"
        author-image="https://picsum.photos/60/60"
      />
    </UContainer>

    <!-- Школы тренера -->
    <PopularSchoolsSection
      v-if="schools?.length"
      class="py-0! common-mb"
      title="Школы тренера"
      :loading="pending"
      :schools="schools"
      tile
      :ui="{
        image: 'max-xl:h-45! max-md:h-32.5!',
        title: 'max-md:line-clamp-2! mb-3',
      }"
    />

    <!-- Гордость тренера -->
    <OurPrideSection
      class="bg-transparent! py-0! common-mb"
      title="Гордость тренера"
      :students="MOCK_STUDENTS as any"
      :loading="pending"
    />

    <!-- Новости и события -->
    <NewsAndEventsSection
      v-if="news?.length"
      class="py-0! common-mb"
      :news="news"
      :loading="pending"
      title="Новости и события"
    />

    <!-- Галерея -->
    <div class="overflow-x-hidden">
      <SPGallerySection
        :images="gallery?.length ? gallery : mockGallery"
        :per-view="galleryImagesAmount"
        class="bg-white py-15 rounded-[30px] max-md:-mx-2.5"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import AboutTrainerSection from '../components/AboutTrainerSection.vue'
import QuoteBlock from '~/components/QuoteBlock.vue'
import OurPrideSection from '~/components/main-page/OurPrideSection.vue'
import NewsAndEventsSection from '~/components/main-page/NewsAndEventsSection.vue'
import type { CatalogAttachment } from '~/api'
import { MOCK_TRAINER_DESCRIPTION } from '~/shared/trainer'
import { MOCK_STUDENTS } from '~/shared/school'
import PopularSchoolsSection from '~/components/main-page/PopularSchoolsSection.vue'

defineOptions({
  name: 'TrainerDetailAbout',
})

const route = useRoute()

const breakpoints = useBreakpointsStore()
const { isMobile } = storeToRefs(breakpoints)
const { isLessThan } = breakpoints

const trainerStore = useTrainerStore()
const { directions, lessonTypes, gallery } = storeToRefs(trainerStore)

const directoryStore = useDirectoryStore()
const { schools, news } = storeToRefs(directoryStore)

const { untilReady } = useRegionReady()

const { pending } = await useAppAsyncData(
  `trainer-about-${route.params.id}`,
  async () => {
    await untilReady()
    await Promise.all([
      directoryStore.fetchSchools(),
      directoryStore.fetchNews(),
    ])
  },
)

const mockGallery: CatalogAttachment[] = [
  {
    id: 1,
    url: 'https://picsum.photos/430/300?random=10',
    name: '',
    original_name: '',
    mime: 'image/jpeg',
    size: 0,
    title: null,
  },
  {
    id: 2,
    url: 'https://picsum.photos/430/300?random=11',
    name: '',
    original_name: '',
    mime: 'image/jpeg',
    size: 0,
    title: null,
  },
  {
    id: 3,
    url: 'https://picsum.photos/430/300?random=12',
    name: '',
    original_name: '',
    mime: 'image/jpeg',
    size: 0,
    title: null,
  },
  {
    id: 4,
    url: 'https://picsum.photos/430/300?random=13',
    name: '',
    original_name: '',
    mime: 'image/jpeg',
    size: 0,
    title: null,
  },
]

const galleryImagesAmount = computed(() => {
  if (import.meta.client) {
    if (isMobile.value) {
      return 1.2
    }

    if (isLessThan('lg')) {
      return 2.2
    }
  }

  return 3
})
</script>

<style lang="scss" scoped></style>
